import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import './KairiDashboard.css';
import { getKairi, getScanner, getRSI2, getStrategyHealth, getUniverse } from '../api/brapi';
import {
  getUser,
  getCompleteConfluenceOpportunities,
  getKairiGovernance,
  updateKairiGovernance,
  getRealtimeAlerts,
  markRealtimeAlertRead,
  markAllRealtimeAlertsRead,
} from '../api/user';
import RSIConnors from './RSIConnors';
import ScannerDashboard from './ScannerDashboard';
import ReversalMonitor from './ReversalMonitor';
import Breakout55Monitor from './Breakout55Monitor';
import StrategyEvidencePanel from './StrategyEvidencePanel';
import OptionsWorkspace from './OptionsWorkspace';
import CarteiraHub from './CarteiraHub';
import MarketDataNotice from './MarketDataNotice';
import DismissibleNotice from './DismissibleNotice';
import AssetDetail from './AssetDetail';
import { getPortfolio, addKairiPosition, removePosition } from '../api/portfolio';
import PortfolioModal from './PortfolioModal';
import WebSocketManager from './WebSocketManager';
import { isB3TradingDay } from '../utils/b3Calendar';
import SmartSearch from './SmartSearch';
import Profile from './Profile';
import { formatTradingDate } from '../utils/dates';
import { formatBacktestPercent, formatProfitFactor, formatStrategyHealthBanner, formatWeightedProfitFactor } from '../utils/backtestRisk';
import {
  Drawer, DrawerBody, DrawerCloseButton, DrawerContent, DrawerHeader, DrawerOverlay,
} from '@chakra-ui/react';

const STRATEGIES = [
  {
    key: 'scanner', name: 'Scanner B3', meta: 'Universo canônico · DB · ao vivo', db: true,
    description: 'Um radar diário para localizar ativos esticados ou com sinais objetivos dentro do universo monitorado.',
    checks: ['Preço e variação do pregão', 'Kairi, RSI(2), IBS e tendência pela média de 200 períodos', 'Volume relativo e sinal consolidado'],
    reading: 'Comece pelos ativos com sinal de compra ou venda e confirme no detalhe se tendência, indicador e liquidez contam a mesma história.',
  },
  {
    key: 'reversal', name: 'Reversão na Tendência', meta: 'SMA200 · MME21 · RSI(2)',
    description: 'Procura pullbacks de curto prazo em ativos cuja tendência principal continua saudável.',
    checks: ['Fechamento acima da SMA200', 'MME21 ascendente nos últimos pregões', 'RSI(2) abaixo de 25 ou pullback próximo à MME21', 'Volume financeiro médio de 21 dias'],
    reading: 'ARMADO indica que o gatilho ocorreu; a entrada é acima da máxima de referência. DISPARADO indica que esse rompimento aconteceu no pregão seguinte.',
  },
  {
    key: 'reversalV2', name: 'Reversão v2', meta: 'Regime IBOV · trailing 2 ATR',
    description: 'Candidata separada da Reversão v1, liberada somente pelo gate persistido.',
    checks: ['Mesma entrada da v1', 'IBOV acima da SMA200', 'IBOV acima de cinco pregões atrás', 'Trailing de 2 ATR14', 'Máximo de 20 sessões'],
    reading: 'Esta aba só aparece quando o snapshot persistido aprova a candidata.',
  },
  {
    key: 'candidateIfr2', name: 'S1 · IFR2 curto', meta: 'SMA200 · RSI(2) · 5 sessões',
    description: 'Setup candidato de reversão curta com filtro de tendência e liquidez.',
    checks: ['Fechamento acima da SMA200', 'RSI(2) abaixo do parâmetro', 'Liquidez média de R$ 20 milhões'],
    reading: 'Esta aba só aparece depois da aprovação do gate persistido da Fase 1d.',
  },
  {
    key: 'candidateSetup91', name: 'S2 · Setup 9.1', meta: 'EMA9 · stop · trailing',
    description: 'Setup candidato baseado na virada da EMA9 e confirmação pela SMA200.',
    checks: ['EMA9 vira para cima', 'Stop na máxima do candle de virada + 1 tick', 'Trailing EMA9 após 1R'],
    reading: 'Esta aba só aparece depois da aprovação do gate persistido da Fase 1d.',
  },
  {
    key: 'candidateInsideBar', name: 'S3 · Inside bar', meta: 'Regime · mãe · alvo R',
    description: 'Setup candidato de continuação após inside bar em regime de alta.',
    checks: ['Close > SMA50 > SMA200', 'IBOV acima da SMA200', 'Entrada stop na máxima da mãe'],
    reading: 'Esta aba só aparece depois da aprovação do gate persistido da Fase 1d.',
  },
  {
    key: 'candidatePullbackRs', name: 'S4 · Pullback EMA20', meta: 'Força relativa · trailing',
    description: 'Setup candidato de pullback em ativos no decil superior de força relativa.',
    checks: ['Força relativa de 126 dias', 'Toque e recuperação da EMA20', 'Trailing após 1R'],
    reading: 'Esta aba só aparece depois da aprovação do gate persistido da Fase 1d.',
  },
  {
    key: 'breakout55', name: 'Rompimento 55/20', meta: 'Donchian55 · Donchian20 · trailing',
    description: 'Rastreador de rompimentos de máxima em tendência, com confirmação de liquidez e plano de saída por trailing.',
    checks: ['Fechamento acima da máxima de 55 pregões', 'Preço acima da SMA200', 'Liquidez financeira média', 'Distância até o gatilho', 'Risco pelo ATR e trailing'],
    reading: 'ARMADO está próximo do gatilho; DISPARADO confirmou o rompimento. A ausência de alvo fixo é intencional: a saída segue o trailing stop.',
  },
  {
    key: 'options', name: 'Opções', meta: 'Calls · Puts · Strikes · ATM',
    description: 'Consulta a cadeia persistida de opções de um ativo da B3 em uma grade profissional.',
    checks: ['Vencimentos disponíveis', 'Calls e puts pareadas por strike', 'Linha ATM, gregas, IV e interesse em aberto'],
    reading: 'Digite o ticker do ativo e use a grade para estudar liquidez, volatilidade e cenários. Os dados são snapshots e não enviam ordens.',
  },
  {
    key: 'setorial', name: 'Análise Setorial', meta: 'Z-Kairi · Sistêmico v3',
    description: 'Compara o comportamento dos setores e destaca quando um movimento parece amplo ou concentrado em poucos ativos.',
    checks: ['Z-Kairi do ativo e do setor', 'Quantidade de ativos em condição extrema', 'Leitura de risco sistêmico e dispersão entre setores'],
    reading: 'Priorize sinais que tenham contexto setorial favorável; um ativo isolado pode ser ruído, enquanto um setor inteiro reforça a leitura.',
  },
  {
    key: 'rsi2', name: 'RSI(2) Connors', meta: 'Clássico US · ~75% win',
    description: 'Aplica o conceito de reversão à média de curto prazo com o RSI de dois períodos.',
    checks: ['RSI(2) em região extrema', 'Preço em relação à média de 200 períodos', 'Candle e histórico recente para confirmar o contexto'],
    reading: 'Use o RSI(2) para identificar excesso de curto prazo, sempre filtrando a direção pela tendência maior. O indicador isolado não é uma ordem.',
  },
  {
    key: 'confluence', name: 'Kairi + Confluência', meta: '~78% win · 6 indicadores',
    description: 'Une seis evidências de reversão à média em um score para evitar decisões baseadas em apenas um indicador.',
    checks: ['Kairi(25) em extremo', 'RSI(2) baixo', 'IBS baixo', 'Preço acima da média de 200 períodos', 'Volume em relação à média de 20 dias', 'Contexto setorial favorável'],
    reading: 'Quanto mais critérios passarem, maior a confluência. O sexto check incorpora o contexto setorial sem substituir a decisão do backend.',
  },
  {
    key: 'carteira', name: 'Carteira', meta: 'Swing Trader · CDR · resultados',
    description: 'Centraliza posições teóricas de ações, mostra a evolução do resultado e preserva o monitoramento automático para swing trades.',
    checks: ['Swing Trader para ações e monitoramento', 'CDR — The Wheel para opções e ciclos', 'Resultados separados por carteira'],
    reading: 'Selecione a carteira que corresponde ao seu plano. A Swing Trader preserva a experiência de ações; a CDR registra a estratégia The Wheel sem enviar ordens.',
  },
];

// These surfaces remain implemented for a later release, but are intentionally
// not exposed in the Phase 1c navigation.
const FEATURE_FLAGS = {
  confluence: false,
  options: false,
};
const isStrategyEnabled = strategy => FEATURE_FLAGS[strategy.key] !== false;

const STRATEGY_GROUPS = [
  {
    key: 'monitoramento',
    label: 'Monitoramento',
    description: 'Acompanhe sinais e gatilhos de entrada',
    strategyKeys: ['scanner', 'reversal', 'reversalV2', 'breakout55', 'options'],
  },
  {
    key: 'candidatos',
    label: 'Candidatos',
    description: 'Setups independentes aguardando ou liberados pelo gate',
    strategyKeys: ['candidateIfr2', 'candidateSetup91', 'candidateInsideBar', 'candidatePullbackRs'],
  },
  {
    key: 'analise',
    label: 'Análise',
    description: 'Aprofunde o contexto técnico e setorial',
    strategyKeys: ['rsi2', 'confluence'],
  },
  {
    key: 'carteira',
    label: 'Carteira',
    description: 'Gerencie suas posições teóricas',
    strategyKeys: ['carteira'],
  },
];

const CANDIDATE_HEALTH_KEYS = {
  candidateIfr2: 'candidateIfr2',
  candidateSetup91: 'candidateSetup91',
  candidateInsideBar: 'candidateInsideBar',
  candidatePullbackRs: 'candidatePullbackRs',
};

const PARAMS = {
  buyThreshold:   -5,    // Kairi ≤ -5%
  sellThreshold:   5,    // Kairi ≥ +5%
  rsiThreshold:   10,    // RSI(2) ≤ 10
  ibsThreshold:  0.2,    // IBS ≤ 0.2
  trendFilter:  true,    // Price > MM200 (mandatory)
  volThreshold:  1.0,    // Volume ≥ 1× 20-day avg
  minConfluence:  90,    // 75/100 scaled to the six-check /120 score
};

// ── Helpers ──────────────────────────────────────────
function classifyAsset(a, conf, params) {
  if (conf.filtered) return 'filtered';
  if (a.kairi >= params.sellThreshold) return 'sell';
  // Manual §4.1 scaled from 75/100 to 90/120 for six checks.
  if (conf.score >= 90 && a.kairi <= params.buyThreshold) return 'buy';
  // 60/100 → 72/120; 40/100 → 48/120.
  if (conf.score >= 72 && a.kairi <= params.buyThreshold + 1) return 'watch';
  if (conf.score >= 48) return 'watch';
  return 'skip';
}

function fmtPrice(p) {
  if (p == null) return '—';
  return `R$\u202F${p.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtPct(v) {
  if (v == null) return '—';
  const s = v > 0 ? '+' : '';
  return `${s}${v.toFixed(2)}%`;
}

function fmtSignalDate(date) {
  return formatTradingDate(date);
}

function formatGovernanceProfitFactor(backtest) {
  if (backtest?.weightedProfitFactorInfinite === true) return '∞';
  const value = Number(backtest?.weightedProfitFactor);
  return Number.isFinite(value) ? value.toFixed(2).replace('.', ',') : '—';
}

function mergeAlerts(current, incoming) {
  const seen = new Set();
  return [...incoming, ...current].filter(alert => {
    const key = alert.id || `${alert.ticker}-${alert.label}-${alert.ts}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  }).slice(0, 20);
}

function readStoredConfluenceAlerts(storageKey) {
  try {
    const saved = JSON.parse(localStorage.getItem(storageKey) || '[]');
    return Array.isArray(saved) ? saved : [];
  } catch (_) {
    localStorage.removeItem(storageKey);
    return [];
  }
}

function saveStoredConfluenceAlerts(storageKey, alerts) {
  localStorage.setItem(storageKey, JSON.stringify(alerts.slice(0, 50)));
}

// ── ScoreCircle ──────────────────────────────────────
function ScoreCircle({ score, color }) {
  const r = 46;
  const c = 2 * Math.PI * r;
  const offset = c * (1 - score / 120);
  return (
    <div className="kr-score-circle">
      <svg viewBox="0 0 110 110" width="110" height="110">
        <circle cx="55" cy="55" r={r} fill="none" stroke="#1c2232" strokeWidth="7" />
        <circle
          cx="55" cy="55" r={r}
          fill="none" stroke={color} strokeWidth="7"
          strokeDasharray={c} strokeDashoffset={offset}
          strokeLinecap="round"
          transform="rotate(-90 55 55)"
          style={{ transition: 'stroke-dashoffset .55s cubic-bezier(.4,0,.2,1)' }}
        />
      </svg>
      <div className="kr-score-inner">
        <div className="kr-score-num" style={{ color }}>{score}/120</div>
        <div className="kr-score-lbl">{score >= 96 ? 'Alta prob.' : score >= 72 ? 'Moderada' : score >= 48 ? 'Baixa' : 'Ignorar'}</div>
      </div>
    </div>
  );
}

// ── StackedIndicators ────────────────────────────────
function StackedIndicators({ asset, params }) {
  const W = 780;
  const H = 106;
  const padL = 46;
  const padR = 50;
  const innerW = W - padL - padR;
  const innerH = H - 28;

  const n = asset.kairiHistory.length;
  const xAt = i => padL + (n > 1 ? (i / (n - 1)) * innerW : innerW / 2);

  // Kairi path
  const kMin = -9;
  const kMax = 9;
  const kY = v => 14 + ((kMax - Math.max(kMin, Math.min(kMax, v))) / (kMax - kMin)) * innerH;
  const kPath = asset.kairiHistory.map((v, i) => `${i === 0 ? 'M' : 'L'} ${xAt(i).toFixed(1)},${kY(v).toFixed(1)}`).join(' ');
  const kLastY = kY(asset.kairi);
  const kLastX = xAt(n - 1);

  // RSI(2) derived series
  const rsiHist = useMemo(() =>
    asset.kairiHistory.map((k, i) =>
      Math.max(0, Math.min(100, 50 - k * 5.5 + Math.sin(i * 1.2) * 7))
    ), [asset.kairiHistory]);
  const rY = v => 14 + ((100 - v) / 100) * innerH;
  const rPath = rsiHist.map((v, i) => `${i === 0 ? 'M' : 'L'} ${xAt(i).toFixed(1)},${rY(v).toFixed(1)}`).join(' ');
  const rLastY = rY(asset.rsi2);

  // IBS derived series
  const ibsHist = useMemo(() =>
    asset.kairiHistory.map((k, i) => {
      const v = 0.5 - k * 0.055 + Math.cos(i * 0.85) * 0.12;
      return Math.max(0, Math.min(1, v));
    }), [asset.kairiHistory]);

  const barW = Math.max(1, (innerW / n) * 0.72);

  const timeLabels = [
    { label: '−60d', pct: 0 }, { label: '−45d', pct: 0.25 }, { label: '−30d', pct: 0.5 },
    { label: '−15d', pct: 0.75 }, { label: 'Hoje', pct: 1 },
  ];

  return (
    <div className="kr-stacked-panels">
      {/* Kairi */}
      <div className="kr-stacked-panel">
        <div className="kr-pan-label">
          KAIRI(25)
          <span className="kr-pv" style={{ color: asset.kairi <= params.buyThreshold ? '#26a69a' : asset.kairi >= params.sellThreshold ? '#ef5350' : '#e8eaf0' }}>
            {fmtPct(asset.kairi)}
          </span>
        </div>
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ display: 'block', width: '100%', height: '100%' }}>
          {/* Overbought / oversold zones */}
          <rect x={padL} y={14} width={innerW} height={innerH * (3 / 18)} fill="rgba(239,83,80,0.05)" />
          <rect x={padL} y={14 + innerH * (12 / 18)} width={innerW} height={innerH * (3 / 18)} fill="rgba(38,166,154,0.06)" />
          {/* Zero line */}
          <line x1={padL} x2={W - padR} y1={14 + innerH / 2} y2={14 + innerH / 2} stroke="#2a3344" strokeDasharray="3 5" />
          {/* Y labels */}
          <text x={padL - 5} y={18}                  fill="#ef5350" fontSize="9" textAnchor="end" fontFamily="JetBrains Mono">+9%</text>
          <text x={padL - 5} y={14 + innerH / 2 + 4} fill="#5e6a85" fontSize="9" textAnchor="end" fontFamily="JetBrains Mono">0</text>
          <text x={padL - 5} y={14 + innerH - 2}     fill="#26a69a" fontSize="9" textAnchor="end" fontFamily="JetBrains Mono">−9%</text>
          {/* Line */}
          <path d={kPath} stroke="#5b9dff" strokeWidth="1.5" fill="none" />
          {/* Today marker */}
          <line x1={kLastX} x2={kLastX} y1={14} y2={H - 14} stroke="#facc15" strokeOpacity="0.4" strokeDasharray="2 4" />
          <circle cx={kLastX} cy={kLastY} r="4" fill={asset.kairi <= params.buyThreshold ? '#26a69a' : '#ef5350'} stroke="#111520" strokeWidth="1.5" />
        </svg>
      </div>

      {/* RSI(2) */}
      <div className="kr-stacked-panel">
        <div className="kr-pan-label">
          RSI(2)
          <span className="kr-pv" style={{ color: asset.rsi2 <= params.rsiThreshold ? '#26a69a' : asset.rsi2 >= 90 ? '#ef5350' : '#e8eaf0' }}>
            {asset.rsi2.toFixed(1)}
          </span>
        </div>
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ display: 'block', width: '100%', height: '100%' }}>
          <rect x={padL} y={14}                             width={innerW} height={innerH * 0.10} fill="rgba(239,83,80,0.06)" />
          <rect x={padL} y={14 + innerH * 0.90}            width={innerW} height={innerH * 0.10} fill="rgba(38,166,154,0.07)" />
          <line x1={padL} x2={W - padR} y1={14 + innerH / 2} y2={14 + innerH / 2} stroke="#2a3344" strokeDasharray="3 5" />
          <text x={padL - 5} y={18}                  fill="#ef5350" fontSize="9" textAnchor="end" fontFamily="JetBrains Mono">90</text>
          <text x={padL - 5} y={14 + innerH / 2 + 4} fill="#5e6a85" fontSize="9" textAnchor="end" fontFamily="JetBrains Mono">50</text>
          <text x={padL - 5} y={14 + innerH - 2}     fill="#26a69a" fontSize="9" textAnchor="end" fontFamily="JetBrains Mono">10</text>
          <path d={rPath} stroke="#a78bfa" strokeWidth="1.5" fill="none" />
          <line x1={kLastX} x2={kLastX} y1={14} y2={H - 14} stroke="#facc15" strokeOpacity="0.4" strokeDasharray="2 4" />
          <circle cx={kLastX} cy={rLastY} r="4" fill={asset.rsi2 <= params.rsiThreshold ? '#26a69a' : '#ef5350'} stroke="#111520" strokeWidth="1.5" />
        </svg>
      </div>

      {/* IBS */}
      <div className="kr-stacked-panel">
        <div className="kr-pan-label">
          IBS
          <span className="kr-pv" style={{ color: asset.ibs <= params.ibsThreshold ? '#26a69a' : '#e8eaf0' }}>
            {asset.ibs.toFixed(2)}
          </span>
        </div>
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ display: 'block', width: '100%', height: '100%' }}>
          <rect x={padL} y={14 + innerH * 0.8} width={innerW} height={innerH * 0.2} fill="rgba(38,166,154,0.07)" />
          <line x1={padL} x2={W - padR} y1={14 + innerH * 0.5} y2={14 + innerH * 0.5} stroke="#2a3344" strokeDasharray="3 5" />
          <text x={padL - 5} y={18}                  fill="#ef5350" fontSize="9" textAnchor="end" fontFamily="JetBrains Mono">1.0</text>
          <text x={padL - 5} y={14 + innerH / 2 + 4} fill="#5e6a85" fontSize="9" textAnchor="end" fontFamily="JetBrains Mono">0.5</text>
          <text x={padL - 5} y={14 + innerH - 2}     fill="#26a69a" fontSize="9" textAnchor="end" fontFamily="JetBrains Mono">0.2</text>
          {ibsHist.map((v, i) => {
            const bH = v * innerH;
            const bY = 14 + innerH - bH;
            const bX = xAt(i) - barW / 2;
            const col = v <= 0.2 ? '#34d399' : v <= 0.4 ? '#facc15' : '#5b9dff';
            return <rect key={i} x={bX.toFixed(1)} y={bY.toFixed(1)} width={barW.toFixed(1)} height={bH.toFixed(1)} fill={col} opacity="0.82" />;
          })}
          {timeLabels.map(({ label, pct }, k) => {
            const i = Math.round(pct * (n - 1));
            return (
              <text key={k} x={xAt(i).toFixed(1)} y={H - 2}
                fill={k === 4 ? '#facc15' : '#3d4860'}
                fontSize="9" textAnchor="middle" fontFamily="JetBrains Mono"
                fontWeight={k === 4 ? '600' : '400'}>
                {label}
              </text>
            );
          })}
        </svg>
      </div>
    </div>
  );
}

function SectorContextBlock({ asset, onReturn }) {
  const context = asset?.sectorContext;
  if (!context) return null;
  const zAsset = context.zAsset ?? context.zScore ?? context.zKairi ?? asset.zScore;
  const zSector = context.zSector ?? context.sectorZ;
  const gap = context.gap;
  const diagnosis = context.diagnosis ?? asset.diagnosis;
  const diagnosisLabel = diagnosis === 'systemic' ? 'Sistêmico' : diagnosis === 'idiosyncratic' ? 'Idiossincrático' : 'Normal';
  const isBelow = context.belowMa200 === true || asset.trendPct <= 0;
  return (
    <section className={`kr-sector-context ${diagnosis === 'idiosyncratic' ? 'idio' : ''}`} data-testid="kairi-sector-context">
      <div className="kr-sector-context-head">
          <div><span className="kr-sector-eyebrow">SETOR E BETA</span><strong>{context.sectorName || asset.sector || 'Setor'}</strong></div>
        <span className={`kr-sector-diagnosis ${diagnosis}`}>{diagnosisLabel}</span>
      </div>
      <div className="kr-sector-metrics">
        <div><span>Z-ativo</span><strong>{zAsset == null ? '—' : `${zAsset > 0 ? '+' : ''}${Number(zAsset).toFixed(2)}σ`}</strong></div>
        <div><span>Z-setor</span><strong>{zSector == null ? '—' : `${zSector > 0 ? '+' : ''}${Number(zSector).toFixed(2)}σ`}</strong></div>
        <div><span>Gap</span><strong>{gap == null ? '—' : `${Number(gap).toFixed(2)}σ`}</strong></div>
        <div><span>β setor</span><strong>{asset.betaSector == null ? '—' : Number(asset.betaSector).toFixed(2)}</strong></div>
        <div><span>β IBOV</span><strong>{asset.betaIbovespa == null ? '—' : Number(asset.betaIbovespa).toFixed(2)}</strong></div>
      </div>
      {isBelow && <div className="kr-mm200-seal">Não operar — abaixo da MM200</div>}
      {diagnosis === 'idiosyncratic' && <div className="kr-idio-seal">queda idiossincrática — não operar</div>}
      <span className="kr-sector-return">Contexto setorial incorporado ao detalhe do ativo.</span>
    </section>
  );
}

function BacktestColumn({ title, data, active, shadow }) {
  if (!data) return <div className="kr-bt-column"><strong>{title}</strong><span className="kr-bt-empty">Sem dados</span></div>;
  return (
    <div className={`kr-bt-column ${active ? 'active' : 'shadow'}`}>
      <div className="kr-bt-column-head"><strong>{title}</strong><span>{active ? 'ATIVA' : shadow ? 'SHADOW' : 'CANDIDATA'}</span></div>
      <div className="kr-bt-metrics">
        <span><small>Pregões</small><b>{data.tradingDays ?? '—'}</b></span>
        <span><small>Trades</small><b>{data.tradesTotal ?? '—'}</b></span>
        <span><small>PF simples</small><b>{formatProfitFactor(data)}</b></span>
        <span><small>Expectância líquida{Number(data.costsBps) > 0 ? ` (${data.costsBps} bps/lado)` : ''}</small><b>{formatBacktestPercent(data.netExpectancyPct)}</b></span>
        <span><small>PF pond. líquido</small><b>{data.weightedNetProfitFactorInfinite ? '∞' : formatWeightedProfitFactor({ ...data, weightedProfitFactor: data.weightedNetProfitFactor, weightedProfitFactorInfinite: data.weightedNetProfitFactorInfinite })}</b></span>
        <span><small>Expect. pond. líquida</small><b>{formatBacktestPercent(data.weightedNetExpectancyPct)}</b></span>
      </div>
    </div>
  );
}

// ── KairiDashboard (Main) ────────────────────────────
function KairiDashboard({ userId, onLogout }) {
  const deepLinkTickerRef = useRef(
    (() => {
      const ticker = new URLSearchParams(window.location.search).get('ticker');
      return /^[A-Z0-9]{4,12}$/.test((ticker || '').toUpperCase()) ? ticker.toUpperCase() : null;
    })()
  );
  const deepLinkStrategyRef = useRef(false);
  const [assets, setAssets]               = useState([]);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState(null);
  const [selectedTicker, setSelectedTicker] = useState('VALE3');
  const [strategy, setStrategy]           = useState('scanner');
  const [navigationGroup, setNavigationGroup] = useState('monitoramento');
  const [strategyGuide, setStrategyGuide] = useState(null);
  const [tableFilter, setTableFilter]     = useState('Todos');

  const [now, setNow]                     = useState(Date.now());
  const [userInfo, setUserInfo]           = useState(null);
  const [showProfile, setShowProfile]     = useState(false);
  const [showSettings, setShowSettings]   = useState(false);

  const [alerts, setAlerts] = useState([]);
  const [backtest, setBacktest] = useState(null);
  const [backtests, setBacktests] = useState(null);
  const [confluenceDecision, setConfluenceDecision] = useState(null);
  const [strategyHealth, setStrategyHealth] = useState(null);
  const [automaticEntries, setAutomaticEntries] = useState(null);
  const [kairiGovernance, setKairiGovernance] = useState(null);
  const [kairiGovernanceLoading, setKairiGovernanceLoading] = useState(false);
  const [kairiGovernanceSaving, setKairiGovernanceSaving] = useState(false);
  const [kairiGovernanceError, setKairiGovernanceError] = useState(null);
  const isAdmin = userInfo?.role === 'admin';
  const candidateApproved = key => (
    strategyHealth?.strategies?.[CANDIDATE_HEALTH_KEYS[key]]?.blocked === false
  );

  const [scannerData, setScannerData] = useState(null);
  const [universeAssets, setUniverseAssets] = useState([]);
  const universeTickers = useMemo(() => universeAssets.map(asset => asset.ticker), [universeAssets]);
  const [detailTicker, setDetailTicker] = useState(null);
  const [strategyFocus, setStrategyFocus] = useState(null);
  const [rsi2Assets, setRsi2Assets] = useState([]);

  const [topbarAlerts, setTopbarAlerts]         = useState([]);
  const [showAlertDrop, setShowAlertDrop]       = useState(false);
  const [entryToast, setEntryToast]             = useState(null);
  const bellRef = useRef(null);
  const knownEntryAlertsRef = useRef(new Set());
  const entryAlertsReadyRef = useRef(false);

  useEffect(() => {
    if (!strategyGuide) return undefined;
    const closeOnEscape = event => {
      if (event.key === 'Escape') setStrategyGuide(null);
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [strategyGuide]);

  useEffect(() => {
    const group = STRATEGY_GROUPS.find(item => item.strategyKeys.includes(strategy));
    if (group) setNavigationGroup(group.key);
  }, [strategy]);

  const [portfolio, setPortfolio]               = useState([]);
  const [portfolioLoading, setPortfolioLoading] = useState(false);
  const [showAddForm, setShowAddForm]           = useState(false);
  const [wsConnected, setWsConnected]           = useState(false);
  const [flashTickers, setFlashTickers]         = useState(new Set());
  const [quoteUpdatedAt, setQuoteUpdatedAt]     = useState(null);
  const [quoteNextUpdateAt, setQuoteNextUpdateAt] = useState(null);

  useEffect(() => {
    if (deepLinkStrategyRef.current) setStrategy('confluence');
  }, []);

  // WebSocket — live prices
  const handleWsMessage = useCallback((priceData) => {
    const quoteMeta = priceData.find(item => item.updatedAt);
    if (quoteMeta) {
      setQuoteUpdatedAt(quoteMeta.updatedAt);
      setQuoteNextUpdateAt(quoteMeta.nextUpdateAt || null);
    }
    setAssets(prev => {
      let changed = false;
      const updatedTickers = [];
      const next = prev.map(a => {
        const upd = priceData.find(p => p.ticker.replace('.SA', '') === a.ticker);
        if (!upd) return a;
        changed = true;
        updatedTickers.push(a.ticker);
        return { ...a, price: upd.value, change: upd.variation };
      });
      if (updatedTickers.length > 0) {
        setFlashTickers(prev => new Set([...prev, ...updatedTickers]));
        setTimeout(() => {
          setFlashTickers(prev => {
            const next = new Set(prev);
            updatedTickers.forEach(t => next.delete(t));
            return next;
          });
        }, 600);
      }
      return changed ? next : prev;
    });
  }, []);

  useEffect(() => {
    getUniverse().then(data => setUniverseAssets(data.assets || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!universeTickers.length) return undefined;
    const wsm = new WebSocketManager(
      (data) => { setWsConnected(true); handleWsMessage(data); },
      () => setWsConnected(false)
    );
    wsm.connect();
    const symbols = universeTickers.map(t => `${t}|S`);
    wsm.subscribeToSymbols(symbols);

    const stateId = setInterval(() => {
      setWsConnected(wsm.isConnected());
    }, 3000);

    return () => {
      clearInterval(stateId);
      wsm.close();
    };
  }, [handleWsMessage, universeTickers]);

  const handleNewAlerts = useCallback((newItems) => {
    if (!isB3TradingDay()) return;
    const stamped = newItems.map(a => ({ ...a, ts: Date.now() }));
    setTopbarAlerts(prev => mergeAlerts(prev, stamped));
    setShowAlertDrop(false);
  }, []);

  const normalizeRealtimeAlert = useCallback(alert => ({
    ...alert,
    id: `realtime-${alert.id}`,
    serverId: alert.id,
    type: ['stop'].includes(alert.triggerType)
      ? 'sell'
      : ['target', 'breakout', 'pending_order'].includes(alert.triggerType) ? 'buy' : 'investigate',
    label: String(alert.message || '').split('\n📰 Contexto:')[0],
    ts: new Date(alert.createdAt || Date.now()).getTime(),
    readAt: alert.readAt || null,
  }), []);

  const refreshRealtimeAlerts = useCallback(async () => {
    if (!userId) return;
    try {
      const result = await getRealtimeAlerts();
      const persisted = (result.alerts || []).map(normalizeRealtimeAlert);
      setTopbarAlerts(previous => {
        const local = previous.filter(alert => !alert.serverId);
        return mergeAlerts(persisted, local);
      });
    } catch (error) {
      console.error('Não foi possível carregar a central de alertas:', error);
    }
  }, [normalizeRealtimeAlert, userId]);

  const clearOpportunityAlerts = useCallback(() => {
    const storageKey = `fatcat:confluence-alerts:${userId}`;
    const hiddenEntries = [...knownEntryAlertsRef.current].map(id => ({ id, hidden: true }));
    saveStoredConfluenceAlerts(storageKey, hiddenEntries);
    markAllRealtimeAlertsRead().catch(error => {
      console.error('Não foi possível marcar os alertas como lidos:', error);
    });
    setTopbarAlerts(previous => previous.map(alert => (
      alert.serverId ? { ...alert, readAt: new Date().toISOString() } : alert
    )));
    setEntryToast(null);
    setShowAlertDrop(false);
  }, [userId]);

  const openAlert = useCallback(alert => {
    if (alert.serverId && !alert.readAt) {
      markRealtimeAlertRead(alert.serverId).catch(error => {
        console.error('Não foi possível marcar o alerta como lido:', error);
      });
      setTopbarAlerts(previous => previous.map(item => (
        item.serverId === alert.serverId ? { ...item, readAt: new Date().toISOString() } : item
      )));
    }
    if (alert.ticker && universeTickers.includes(alert.ticker)) {
      setSelectedTicker(alert.ticker);
      setDetailTicker(alert.ticker);
    }
  }, [universeTickers]);

  useEffect(() => {
    refreshRealtimeAlerts();
    const onRealtimeAlert = event => {
      const alert = normalizeRealtimeAlert(event.detail);
      setTopbarAlerts(previous => mergeAlerts([alert], previous));
      setEntryToast(alert);
    };
    window.addEventListener('fatcat:realtime-alert', onRealtimeAlert);
    return () => window.removeEventListener('fatcat:realtime-alert', onRealtimeAlert);
  }, [normalizeRealtimeAlert, refreshRealtimeAlerts]);

  useEffect(() => {
    if (!entryToast) return undefined;
    const timeout = window.setTimeout(() => setEntryToast(null), 9000);
    return () => window.clearTimeout(timeout);
  }, [entryToast]);

  const refreshCompleteEntryAlerts = useCallback(async () => {
    if (!userId) return;
    if (!isB3TradingDay()) return;
    try {
      const { opportunities = [] } = await getCompleteConfluenceOpportunities();
      const currentEntries = opportunities.map(opportunity => ({
        id: `confluence-${opportunity.signalDate}-${opportunity.ticker}`,
        type: 'buy',
        ticker: opportunity.ticker,
        label: 'Entrada confirmada · Confluência 100%',
        ts: Date.now(),
      }));
      const storageKey = `fatcat:confluence-alerts:${userId}`;

      if (!entryAlertsReadyRef.current) {
        const savedItems = readStoredConfluenceAlerts(storageKey);
        const hiddenItems = savedItems.filter(alert => alert.hidden);
        const visibleSavedItems = savedItems.filter(alert => !alert.hidden);
        savedItems.forEach(alert => knownEntryAlertsRef.current.add(alert.id));
        const unseenEntries = currentEntries.filter(alert => !knownEntryAlertsRef.current.has(alert.id));
        unseenEntries.forEach(alert => knownEntryAlertsRef.current.add(alert.id));
        const combined = mergeAlerts(visibleSavedItems, unseenEntries);
        setTopbarAlerts(previous => mergeAlerts(previous, combined));
        if (unseenEntries.length > 0) setEntryToast(unseenEntries[0]);
        saveStoredConfluenceAlerts(storageKey, [...hiddenItems, ...combined]);
        entryAlertsReadyRef.current = true;
        return;
      }

      const newEntries = currentEntries.filter(alert => !knownEntryAlertsRef.current.has(alert.id));
      if (!newEntries.length) return;
      newEntries.forEach(alert => knownEntryAlertsRef.current.add(alert.id));
      setTopbarAlerts(previous => {
        const next = mergeAlerts(previous, newEntries);
        const hiddenItems = readStoredConfluenceAlerts(storageKey).filter(alert => alert.hidden);
        const confluenceItems = next.filter(alert => alert.id?.startsWith('confluence-'));
        saveStoredConfluenceAlerts(storageKey, [...hiddenItems, ...confluenceItems]);
        return next;
      });
      setEntryToast(newEntries[0]);
    } catch (error) {
      console.error('Não foi possível atualizar os alertas de entrada:', error);
    }
  }, [userId]);

  useEffect(() => {
    function handleClick(e) {
      if (bellRef.current && !bellRef.current.contains(e.target)) {
        setShowAlertDrop(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const params = PARAMS;

  // Clock
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // Load user info
  useEffect(() => {
    if (userId) {
      getUser(userId).then(u => setUserInfo(u)).catch(() => {});
    }
  }, [userId]);

  const loadKairiGovernance = useCallback(async () => {
    if (!userId) {
      setKairiGovernance(null);
      return;
    }
    setKairiGovernanceLoading(true);
    try {
      const data = await getKairiGovernance();
      setKairiGovernance(data);
      setKairiGovernanceError(null);
    } catch (err) {
      setKairiGovernanceError('Não foi possível carregar a preferência da variante.');
    } finally {
      setKairiGovernanceLoading(false);
    }
  }, [userId]);

  useEffect(() => { loadKairiGovernance(); }, [loadKairiGovernance]);

  const handleKairiGovernanceChange = useCallback(async (mode) => {
    if (!kairiGovernance || mode === kairiGovernance.mode || kairiGovernanceSaving) return;
    const previous = kairiGovernance;
    setKairiGovernance({ ...previous, mode });
    setKairiGovernanceSaving(true);
    setKairiGovernanceError(null);
    try {
      await updateKairiGovernance(mode);
      await loadKairiGovernance();
    } catch (err) {
      setKairiGovernance(previous);
      setKairiGovernanceError('Não foi possível salvar a preferência. A seleção anterior foi restaurada.');
    } finally {
      setKairiGovernanceSaving(false);
    }
  }, [kairiGovernance, kairiGovernanceSaving, loadKairiGovernance]);

  // Fetch scanner data for the consolidated scanner and navigation counts.
  useEffect(() => {
    getScanner().then(d => setScannerData(d)).catch(() => {});
  }, []);

  useEffect(() => {
    if (strategy !== 'confluence') {
      setShowAlertDrop(false);
      setEntryToast(null);
      return undefined;
    }
    refreshCompleteEntryAlerts();
    const interval = window.setInterval(refreshCompleteEntryAlerts, 60000);
    const reloadHandler = () => refreshCompleteEntryAlerts();
    window.addEventListener('fatcat:data-reload', reloadHandler);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('fatcat:data-reload', reloadHandler);
    };
  }, [refreshCompleteEntryAlerts, strategy]);

  // Fetch RSI2 data for SmartSearch (cached by backend)
  useEffect(() => {
    if (!universeTickers.length) return;
    getRSI2(universeTickers.join(',')).then(d => {
      setRsi2Assets(d.assets || []);
    }).catch(() => {});
  }, [universeTickers]);

  const loadAssets = useCallback(async () => {
    if (!universeTickers.length) return;
    setLoading(true);
    setError(null);
    try {
      const deepLinkedTicker = deepLinkTickerRef.current;
      const tickersToLoad = deepLinkedTicker && !universeTickers.includes(deepLinkedTicker)
        ? [...universeTickers, deepLinkedTicker]
        : universeTickers;
      const [resp, health] = await Promise.all([
        getKairi(tickersToLoad.join(',')),
        getStrategyHealth().catch(() => null),
      ]);
      const data = resp.assets || (Array.isArray(resp) ? resp : []);
      if (data.length === 0) throw new Error('Nenhum dado retornado');
      setAssets(data);
       // `backtest` is the backend-declared active result. The comparison
       // variants are never promoted locally; aliases are only shape-tolerant.
       if (resp.backtests) setBacktests(resp.backtests);
       if (resp.backtest) setBacktest(resp.backtest);
       else if (resp.backtests) {
         setBacktest(resp.backtests.kairi || resp.backtests.baseline || resp.backtests.base || null);
       }
       if (resp.activeConfluenceStrategy || resp.variantPromotion) {
         setConfluenceDecision({
           activeConfluenceStrategy: resp.activeConfluenceStrategy,
           variantPromotion: resp.variantPromotion,
         });
       }
      setStrategyHealth(health);
      setAutomaticEntries(resp.automaticEntries || null);
      setSelectedTicker(data.some(asset => asset.ticker === deepLinkedTicker) ? deepLinkedTicker : data[0].ticker);
      // Generate real alerts from enriched data using classifyAsset
      const generated = data.map((a, i) => {
        const conf = a.conf;
        if (!conf?.checks) return null;
        const kind = classifyAsset(a, conf, PARAMS);
        if (kind === 'skip') return null;
        const kindLabel = { buy: 'Setup confirmado', watch: 'Aguardando confirmação', filtered: 'Sinal filtrado', sell: 'Saída' }[kind] || kind;
        const icon = { buy: '↗', watch: '•', filtered: '✕', sell: '▣' }[kind] || '•';
        const kaiF = typeof a.kairi === 'number' ? a.kairi.toFixed(1) : '—';
        const rsiF = typeof a.rsi2  === 'number' ? a.rsi2.toFixed(1)  : '—';
        const desc = kind === 'buy'
          ? `Kairi ${kaiF}%, RSI(2) ${rsiF}, score ${conf.score}/120`
          : kind === 'watch'
          ? `Score ${conf.score}/120 · Kairi ${kaiF}%`
          : kind === 'filtered'
          ? `Kairi ${kaiF}% mas abaixo da MM200`
          : `Kairi ${kaiF}%`;
        return { id: i + 1, kind, ticker: a.ticker, title: `${a.ticker} — ${kindLabel}`, desc, icon, conf: conf.score, ts: Date.now() - i * 120_000 };
      }).filter(Boolean);
      setAlerts(generated);
    } catch (e) {
      console.error('Erro ao carregar Kairi:', e);
      setError(e.message || 'Falha ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, [universeTickers]);

  // Load data on mount + after daily server reload
  useEffect(() => { loadAssets(); }, [loadAssets]);

  useEffect(() => {
    const handler = () => loadAssets();
    window.addEventListener('fatcat:data-reload', handler);
    return () => window.removeEventListener('fatcat:data-reload', handler);
  }, [loadAssets]);

  // Portfolio
  const loadPortfolio = useCallback(async () => {
    setPortfolioLoading(true);
    try {
      const data = await getPortfolio();
      setPortfolio(data.positions || []);
    } catch (e) {
      console.error('Portfolio load error:', e);
    } finally {
      setPortfolioLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPortfolio();
    const id = setInterval(loadPortfolio, 30000);
    return () => clearInterval(id);
  }, [loadPortfolio]);

  // O endpoint entrega o score canônico; ranking e detalhe usam este mesmo objeto.
  const enriched = useMemo(
    () => assets.filter(asset => asset.conf?.checks?.length === 6),
    [assets],
  );

  const selected = enriched.find(a => a.ticker === selectedTicker) || enriched[0];

  const handleConsolidatedAssetClick = useCallback((ticker, strategyKey) => {
    if (strategyKey === 'scanner') {
      setDetailTicker(ticker);
      return;
    }
    if (strategyKey === 'setorial' || (!FEATURE_FLAGS[strategyKey] && ['confluence', 'options'].includes(strategyKey))) {
      strategyKey = 'scanner';
    }
    setDetailTicker(null);
    setStrategyFocus(ticker);
    setSelectedTicker(ticker);
    setStrategy(strategyKey);
  }, []);


  const handleRemovePosition = useCallback(async (id, e) => {
    e.stopPropagation();
    try {
      await removePosition(id);
      setPortfolio(prev => prev.filter(p => p.id !== id));
    } catch (e) {
      console.error('Remove position error:', e);
    }
  }, [userId]);

  // Sorted + filtered list
  const visible = useMemo(() => {
    let list = enriched;
    if (tableFilter !== 'Todos') {
      const t = { 'Compra': 'buy', 'Venda': 'sell', 'Watch': 'watch' }[tableFilter];
      if (t) list = list.filter(a => classifyAsset(a, a.conf, params) === t);
    }
    return [...list].sort((x, y) => y.conf.score - x.conf.score);
  }, [enriched, tableFilter]);

  const counts = useMemo(() => {
    let confirmed = 0, watching = 0, filtered = 0;
    enriched.forEach(a => {
      const sig = classifyAsset(a, a.conf, params);
      if (sig === 'buy')      confirmed++;
      else if (sig === 'watch')   watching++;
      else if (sig === 'filtered') filtered++;
    });
    return { confirmed, watching, filtered };
  }, [enriched]);

  const selectedSig = selected ? classifyAsset(selected, selected.conf, params) : 'skip';
  const governanceBacktest = kairiGovernance?.selectedBacktest || null;
  const selectedHealthKey = kairiGovernance?.effectiveStrategy === 'kairi_setorial'
    ? 'kairiSetorial'
    : selected?.tier === 'SC' ? 'confluenceSc' : 'confluenceBlue';
  const riskBacktest = strategyHealth?.strategies?.[selectedHealthKey]
    || strategyHealth?.strategies?.confluence
    || (governanceBacktest ? {
      ...governanceBacktest,
      available: governanceBacktest.weightedAvailable === true,
      blocked: kairiGovernance?.allowed !== true,
    } : {
      available: false,
      blocked: true,
    });
  const healthUnavailable = !governanceBacktest && riskBacktest.available === false;
  const backtestBlocked = riskBacktest.blocked !== false;
  const shortBacktest = !healthUnavailable && Number(riskBacktest.tradingDays) < 252;
  const entryBlockTitle = healthUnavailable
    ? 'Saúde da estratégia indisponível — entrada bloqueada'
    : riskBacktest.rule?.text || 'Regra canônica de saúde não atendida — entrada bloqueada';
  const backtestPeriod = governanceBacktest?.periodStart && governanceBacktest?.periodEnd
    ? `${formatTradingDate(governanceBacktest.periodStart)}–${formatTradingDate(governanceBacktest.periodEnd)}`
    : backtest?.periodStart && backtest?.periodEnd
    ? `${formatTradingDate(backtest.periodStart)}–${formatTradingDate(backtest.periodEnd)}`
    : null;
  const automaticIntradayBlocked = kairiGovernance ? kairiGovernance.allowed === false : backtestBlocked || automaticEntries?.allowed === false;
  const costsBps = Number(riskBacktest?.costsBps);
  const hasBacktestCosts = Number.isFinite(costsBps) && costsBps > 0;
  const netExpectancy = hasBacktestCosts && Number.isFinite(Number(riskBacktest?.netExpectancyPct))
    ? riskBacktest.netExpectancyPct
    : riskBacktest?.expectancyPct;
  const expectancyLabel = hasBacktestCosts
    ? 'Expectância líquida (custo por ativo (modelo v1))'
    : 'Expectância líquida';
  const scoreColor  = selected
    ? (selected.conf.score >= 84 ? '#26a69a' : selected.conf.score >= 60 ? '#facc15' : '#ef5350')
    : '#5e6a85';

  // Assets combinados para o SmartSearch: scanner + RSI2 + Kairi+Confluência
  const searchAssets = useMemo(() => {
    const scannerAssets = scannerData?.assets || [];

    // RSI2 map
    const rsi2Map = {};
    rsi2Assets.forEach(a => {
      rsi2Map[a.ticker] = { rsi2Status: a.status, rsi2Value: typeof a.rsi2 === 'number' ? a.rsi2 : null };
    });

    // Confluence map (from enriched Kairi assets)
    const confMap = {};
    enriched.forEach(a => {
      const kind = classifyAsset(a, a.conf, params);
      confMap[a.ticker] = {
        confluenceSignal: kind === 'buy' ? 'buy' : kind === 'watch' ? 'watch' : 'skip',
        confluenceScore:  a.conf?.score ?? null,
      };
    });

    const base = scannerAssets.length > 0
      ? scannerAssets
      : enriched.map(a => {
          const kind = classifyAsset(a, a.conf, params);
          return {
            ...a,
            signal: kind === 'buy' ? 'buy' : kind === 'watch' ? 'watch' : 'skip',
            diagnosis: kind === 'filtered' ? 'filtered' : 'normal',
          };
        });

    return base.map(a => ({
      ...a,
      ...(rsi2Map[a.ticker] || { rsi2Status: null, rsi2Value: null }),
      ...(confMap[a.ticker] || { confluenceSignal: null, confluenceScore: null }),
    }));
  }, [scannerData, enriched, rsi2Assets]);

  const handleSearchSelect = useCallback((asset) => {
    // Navega para o setup mais relevante do ativo selecionado
    if (asset.signal === 'buy' || asset.signal === 'investigate' || asset.zKairi != null) {
      setStrategy('scanner');
      setDetailTicker(asset.ticker);
    } else if (asset.rsi2Status === 'buy' || asset.rsi2Status === 'approaching') {
      setStrategy('rsi2');
    } else if (asset.confluenceSignal === 'buy') {
      setStrategy('scanner');
    } else {
      setStrategy('scanner');
      setDetailTicker(asset.ticker);
    }
  }, []);

  // ── Render ──
  return (
    <div className={`kr-root ${strategy === 'reversal' ? 'kr-root-page-scroll' : ''}`}>
      {strategy === 'confluence' && entryToast && (
        <div className="kr-entry-toast" role="status" aria-live="assertive">
          <span className="kr-entry-toast-icon">↗</span>
          <div>
            <div className="kr-entry-toast-title">Entrada confirmada</div>
            <div className="kr-entry-toast-text">
              {entryToast.ticker} · Confluência completa. Disponível na tela e nos canais ativados.
            </div>
          </div>
          <button type="button" className="kr-entry-toast-close" onClick={() => setEntryToast(null)} aria-label="Fechar alerta">×</button>
        </div>
      )}

      {/* TopBar */}
      <div className="kr-topbar">
        <div className="kr-brand">
          <img src="/fatcat-logo-full.png" alt="Fatcat" className="kr-logo-full" />
        </div>

        <SmartSearch assets={searchAssets} onSelect={handleSearchSelect} />

        <div className="kr-topbar-right">
          <div className="kr-topbar-sep" />
          {wsConnected
            ? <div className="kr-live-badge"><span className="kr-live-dot" />AO VIVO</div>
            : <div className="kr-live-badge kr-live-badge-off"><span className="kr-live-dot-off" />FECHADO</div>
          }
          <details className="kr-quote-details">
            <summary className="kr-quote-summary" aria-label="Detalhes das cotações">
              <span className="kr-quote-summary-dot" />
              <span className="kr-quote-summary-label">Cotações</span>
              <strong>
                {quoteUpdatedAt
                  ? new Date(quoteUpdatedAt).toLocaleTimeString('pt-BR', {
                      timeZone: 'America/Sao_Paulo',
                      hour: '2-digit',
                      minute: '2-digit',
                    })
                  : '—'}
              </strong>
            </summary>
            <div className="kr-quote-popover" aria-live="polite">
              <div>
                <span>Última leitura</span>
                <strong>{quoteUpdatedAt
                  ? new Date(quoteUpdatedAt).toLocaleTimeString('pt-BR', {
                      timeZone: 'America/Sao_Paulo',
                      hour: '2-digit',
                      minute: '2-digit',
                    })
                  : '—'}</strong>
              </div>
              <div>
                <span>Próxima atualização</span>
                <strong>{quoteNextUpdateAt
                  ? new Date(quoteNextUpdateAt).toLocaleTimeString('pt-BR', {
                      timeZone: 'America/Sao_Paulo',
                      hour: '2-digit',
                      minute: '2-digit',
                    })
                  : '—'}</strong>
              </div>
            </div>
          </details>
          <div className="kr-clock">
            {new Date(now).toLocaleTimeString('pt-BR', {
              timeZone: 'America/Sao_Paulo',
              hour12: false,
            })}
          </div>

          <div className="kr-alert-bell" ref={bellRef}>
            <button
              className={`kr-bell-btn${topbarAlerts.some(alert => !alert.readAt) ? ' has-alerts' : ''}`}
              onClick={() => setShowAlertDrop(d => !d)}
              title="Central de alertas"
            >
              🔔
              {topbarAlerts.some(alert => !alert.readAt) && (
                <span className={`kr-bell-badge ${
                  topbarAlerts.every(a => a.type === 'buy' || a.type === 'investigate') ? 'buy'
                  : topbarAlerts.some(a => a.type === 'buy') ? 'mix' : ''
                }`}>
                  {topbarAlerts.filter(alert => !alert.readAt).length > 9
                    ? '9+'
                    : topbarAlerts.filter(alert => !alert.readAt).length}
                </span>
              )}
            </button>

            {showAlertDrop && (
              <div className="kr-alert-dropdown">
                <div className="kr-alert-drop-head">
                  <span>Central de alertas</span>
                  {topbarAlerts.length > 0 && (
                    <button className="kr-alert-drop-clear" onClick={clearOpportunityAlerts}>
                      Marcar lidos
                    </button>
                  )}
                </div>
                {topbarAlerts.length === 0 ? (
                  <div className="kr-alert-drop-empty">Nenhum alerta ainda — aguardando novas oportunidades</div>
                ) : (
                  <div className="kr-alert-drop-list">
                    {topbarAlerts.map((a, i) => {
                      const elapsed = Math.floor((Date.now() - a.ts) / 1000);
                      const timeStr = elapsed < 60 ? `${elapsed}s` : `${Math.floor(elapsed / 60)}min`;
                      return (
                          <button
                            key={a.id || i}
                            type="button"
                            className={`kr-alert-drop-item${a.readAt ? ' is-read' : ''}`}
                            onClick={() => openAlert(a)}
                          >
                            <span className={`kr-alert-drop-dot ${a.type}`} />
                            <div className="kr-alert-drop-body">
                              <div className="kr-alert-drop-ticker">{a.ticker}</div>
                              <div className="kr-alert-drop-label">{a.label}</div>
                              {a.newsContext?.context && (
                                <div className="kr-alert-drop-news">
                                  📰 {a.newsContext.context}
                                  <span>IA · informativo</span>
                                </div>
                              )}
                            </div>
                            <span className="kr-alert-drop-time">{timeStr} atrás</span>
                          </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          <button className="kr-icon-btn" title="Atualizar dados" onClick={loadAssets} disabled={loading}>↻ Atualizar</button>

          {/* User profile */}
          {userInfo && (
            <div className="kr-profile" onClick={() => setShowProfile(p => !p)}>
              <div className="kr-avatar">
                {(userInfo.username || userInfo.email || '?').charAt(0).toUpperCase()}
              </div>
              <div className="kr-profile-info">
                <div className="kr-profile-name">{userInfo.username || userInfo.email}</div>
                <div className="kr-profile-role">Trader · PRO</div>
              </div>
              {showProfile && (
                <div className="kr-profile-dropdown">
                  <div className="kr-profile-dropdown-header">
                    <div className="kr-profile-dropdown-avatar">
                      {(userInfo.username || userInfo.email || '?').charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div className="kr-profile-dropdown-name">{userInfo.username || '—'}</div>
                      <div className="kr-profile-dropdown-email">{userInfo.email || '—'}</div>
                    </div>
                  </div>
                  <div className="kr-profile-dropdown-divider" />
                  <button
                    type="button"
                    className="kr-profile-dropdown-settings"
                    onClick={(event) => {
                      event.stopPropagation();
                      setShowProfile(false);
                      setShowSettings(true);
                    }}
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z"/>
                      <path d="M19.4 15a1.7 1.7 0 0 0 .34 1.88l.06.06-2.1 2.1-.06-.06a1.7 1.7 0 0 0-1.88-.34 1.7 1.7 0 0 0-1.03 1.56V20.3h-3v-.1A1.7 1.7 0 0 0 10.7 18.6a1.7 1.7 0 0 0-1.88.34l-.06.06-2.1-2.1.06-.06A1.7 1.7 0 0 0 7.06 15a1.7 1.7 0 0 0-1.56-1.03H5.4v-3h.1A1.7 1.7 0 0 0 7.06 9.94a1.7 1.7 0 0 0-.34-1.88l-.06-.06 2.1-2.1.06.06a1.7 1.7 0 0 0 1.88.34 1.7 1.7 0 0 0 1.03-1.56V4.6h3v.14a1.7 1.7 0 0 0 1.03 1.56 1.7 1.7 0 0 0 1.88-.34l.06-.06 2.1 2.1-.06.06a1.7 1.7 0 0 0-.34 1.88 1.7 1.7 0 0 0 1.56 1.03h.1v3h-.1A1.7 1.7 0 0 0 19.4 15Z"/>
                    </svg>
                    Configurações e alertas
                  </button>
                  {userInfo?.role === 'admin' && (
                    <a className="kr-profile-dropdown-admin" href="#admin">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                        <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                      </svg>
                      Área administrativa
                    </a>
                  )}
                  <div className="kr-profile-dropdown-divider" />
                  <button className="kr-profile-dropdown-logout" onClick={onLogout}>
                    ⏻ Sair da plataforma
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Logout fallback when no userInfo yet */}
          {!userInfo && onLogout && (
            <button className="kr-logout-btn" onClick={onLogout}>⏻ Sair</button>
          )}
        </div>
      </div>

      <Drawer isOpen={showSettings} placement="right" onClose={() => setShowSettings(false)} size="sm">
        <DrawerOverlay />
        <DrawerContent bg="#10151d" color="#e8eaf0">
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px">Configurações</DrawerHeader>
          <DrawerBody pb={8}>
            <Profile userId={userId} onClose={() => setShowSettings(false)} />
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {/* Aviso técnico comum: aparece uma vez, fora das abas de estratégia. */}
      {scannerData?.marketData && (
        <MarketDataNotice marketData={scannerData.marketData} isAdmin={isAdmin} />
      )}
      {/* Strategy navigation */}
      <div className="kr-strategy-selector">
        <div className="kr-strategy-groups" role="tablist" aria-label="Áreas da plataforma">
          {STRATEGY_GROUPS.map(group => {
            const groupStrategies = STRATEGIES.filter(item => (
              group.strategyKeys.includes(item.key)
              && (!CANDIDATE_HEALTH_KEYS[item.key] || candidateApproved(item.key))
            ));
            if (group.key === 'candidatos' && !groupStrategies.length) return null;
            const groupActive = navigationGroup === group.key;
            const currentStrategy = groupStrategies.find(item => item.key === strategy);
            const isPortfolio = group.key === 'carteira';
            return (
              <button
                type="button"
                role="tab"
                aria-selected={groupActive}
                className={`kr-strategy-group ${groupActive ? 'active' : ''} ${isPortfolio ? 'portfolio' : ''}`}
                key={group.key}
                onClick={() => {
                  setNavigationGroup(group.key);
                  if (isPortfolio) {
                    setStrategy('carteira');
                    setDetailTicker(null);
                  } else if (!currentStrategy) {
                    setStrategy(groupStrategies[0].key);
                    setDetailTicker(null);
                  }
                }}
                title={group.description}
              >
                <span className="kr-group-label">{group.label}</span>
                <span className="kr-group-current">{currentStrategy ? currentStrategy.name : `${groupStrategies.length} estratégias`}</span>
                <span className="kr-group-chevron" aria-hidden="true">{isPortfolio ? '↗' : '⌄'}</span>
              </button>
            );
          })}
        </div>

        {navigationGroup !== 'carteira' && (
          <div className="kr-strategy-subnav" role="tablist" aria-label={`Estratégias de ${navigationGroup === 'monitoramento' ? 'monitoramento' : 'análise'}`}>
            {STRATEGY_GROUPS.find(group => group.key === navigationGroup)?.strategyKeys.map(key => {
              const strategyItem = STRATEGIES.find(item => item.key === key);
              if (!strategyItem || !isStrategyEnabled(strategyItem)) return null;
              if (CANDIDATE_HEALTH_KEYS[strategyItem.key] && !candidateApproved(strategyItem.key)) return null;
              if (strategyItem.key === 'reversalV2'
                && strategyHealth?.strategies?.reversalV2?.blocked !== false) return null;
              return (
                <div className="kr-strategy-tab-wrap" key={strategyItem.key}>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={strategy === strategyItem.key}
                    className={`kr-strategy-tab ${strategy === strategyItem.key ? 'active' : ''}`}
                    onClick={() => { setStrategy(strategyItem.key); setDetailTicker(null); }}
                  >
                    <span className="kr-tab-name">{strategyItem.name}</span>
                  </button>
                  <button
                    type="button"
                    className="kr-strategy-info"
                    onClick={() => setStrategyGuide(strategyItem)}
                    aria-label={`Como analisar a estratégia ${strategyItem.name}`}
                    title={`Como analisar ${strategyItem.name}`}
                    data-testid={`button-strategy-info-${strategyItem.key}`}
                  >
                    !
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {strategyGuide && (
        <div className="kr-strategy-modal-backdrop" role="presentation" onMouseDown={() => setStrategyGuide(null)}>
          <section
            className="kr-strategy-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="strategy-guide-title"
            onMouseDown={event => event.stopPropagation()}
          >
            <header>
              <div>
                <span>GUIA DA ESTRATÉGIA</span>
                <h2 id="strategy-guide-title">{strategyGuide.name}</h2>
                <p>{strategyGuide.meta}</p>
              </div>
              <button type="button" onClick={() => setStrategyGuide(null)} aria-label="Fechar explicação da estratégia">×</button>
            </header>
            <div className="kr-strategy-modal-body">
              <p className="kr-strategy-modal-description">{strategyGuide.description}</p>
              <div className="kr-strategy-modal-section">
                <h3>O que o monitor analisa</h3>
                <ul>
                  {strategyGuide.checks.map(check => <li key={check}>{check}</li>)}
                </ul>
              </div>
              <div className="kr-strategy-modal-reading">
                <h3>Como interpretar</h3>
                <p>{strategyGuide.reading}</p>
              </div>
              <p className="kr-strategy-modal-note">Ferramenta educacional: confirme seu plano de risco antes de qualquer decisão.</p>
            </div>
          </section>
        </div>
      )}

      {/* Estratégias com tela própria */}
      {strategy === 'scanner' ? (
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          {detailTicker ? (
            <AssetDetail
              ticker={detailTicker}
              onBack={() => setDetailTicker(null)}
            />
          ) : (
            <ScannerDashboard universeTickers={universeTickers} onAssetClick={handleConsolidatedAssetClick} onNewAlerts={handleNewAlerts} />
          )}
        </div>
      ) : strategy === 'reversal' ? (
        <div className="kr-reversal-screen">
          <ReversalMonitor focusTicker={strategyFocus} />
        </div>
      ) : strategy === 'reversalV2' ? (
        <div className="kr-reversal-screen">
          <StrategyEvidencePanel
            strategy="reversal_v2"
            title="Evidência · Reversão v2"
            strategyHealth={strategyHealth}
          />
        </div>
      ) : CANDIDATE_HEALTH_KEYS[strategy] ? (
        <div className="kr-reversal-screen">
          <StrategyEvidencePanel
            strategy={strategy.replace(/^candidate/, 'candidate_').replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`)}
            title={`Evidência · ${STRATEGIES.find(item => item.key === strategy)?.name || 'Candidato'}`}
            strategyHealth={strategyHealth}
          />
        </div>
      ) : strategy === 'breakout55' ? (
        <div className="kr-reversal-screen">
          <Breakout55Monitor focusTicker={strategyFocus} />
        </div>
      ) : strategy === 'options' ? (
        <OptionsWorkspace />
      ) : strategy === 'rsi2' ? (
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          <RSIConnors universeTickers={universeTickers} onLogout={onLogout} focusTicker={strategyFocus} />
        </div>
      ) : strategy === 'carteira' ? (
         <CarteiraHub />
      ) : (<>
      <section className="kr-governance" aria-label="Governança da variante Kairi">
        <div className="kr-governance-copy">
          <div className="kr-governance-title">
            Variante ativa
            {kairiGovernance?.candidate && (
              <span className="kr-governance-badge" data-testid="kairi-governance-badge">
                Variante com filtro setorial: PF pond {formatGovernanceProfitFactor(kairiGovernance.candidate)} · {kairiGovernance.candidate.tradesTotal ?? '—'} trades
                {Number(kairiGovernance.candidate.tradesTotal) < 100 && ' · aguardando 100'}
              </span>
            )}
          </div>
          {(!kairiGovernance || kairiGovernance.mode === 'automatic') && (
            <p>A variante padrão ativa permanece canônica até 100 trades; depois, a candidata só é usada se se qualificar.</p>
          )}
        </div>
        <div className="kr-governance-controls">
          <label htmlFor="kairi-governance-mode">Modo da variante</label>
          <select
            id="kairi-governance-mode"
            value={kairiGovernance?.mode || 'automatic'}
            onChange={event => handleKairiGovernanceChange(event.target.value)}
            disabled={!kairiGovernance || kairiGovernanceLoading || kairiGovernanceSaving}
            aria-describedby="kairi-governance-status"
          >
            <option value="automatic">Padrão</option>
            <option value="kairi">Canônica</option>
            <option value="kairi_setorial">Filtro setorial</option>
          </select>
          <span id="kairi-governance-status" className="kr-governance-status" role="status">
            {kairiGovernanceSaving ? 'Salvando…' : kairiGovernanceError || (kairiGovernanceLoading ? 'Carregando…' : '')}
          </span>
        </div>
      </section>
      {!healthUnavailable && (
        <DismissibleNotice noticeKey="kairi-backtest-risk" eventKey={backtestBlocked ? 'blocked' : 'released'} closeLabel="Fechar aviso de saúde da estratégia Kairi" className="kr-backtest-risk-banner" role={backtestBlocked ? 'alert' : 'status'} data-testid="kairi-backtest-risk-banner">
          {formatStrategyHealthBanner(riskBacktest)}
        </DismissibleNotice>
      )}
      {strategyHealth?.strategies?.kairiSetorial && (
        <DismissibleNotice noticeKey="kairi-setorial-backtest-risk" eventKey={strategyHealth.strategies.kairiSetorial.blocked ? 'blocked' : 'released'} closeLabel="Fechar aviso de saúde da estratégia Kairi Setorial" className="kr-backtest-risk-banner" role={strategyHealth.strategies.kairiSetorial.blocked ? 'alert' : 'status'} data-testid="kairi-setorial-backtest-risk-banner">
          Kairi Setorial — {formatStrategyHealthBanner(strategyHealth.strategies.kairiSetorial)}
        </DismissibleNotice>
      )}
      {healthUnavailable && (
        <DismissibleNotice noticeKey="kairi-health-unavailable" eventKey="unavailable" closeLabel="Fechar aviso de indisponibilidade da estratégia" className="kr-backtest-sample-warning" role="status">
          Saúde da estratégia indisponível — novas entradas permanecem bloqueadas.
        </DismissibleNotice>
      )}
      {shortBacktest && (
        <DismissibleNotice noticeKey="kairi-short-sample" eventKey="short-sample" closeLabel="Fechar aviso de amostra curta" className="kr-backtest-sample-warning" role="status" data-testid="kairi-backtest-sample-warning">
          Amostra curta: {riskBacktest.tradingDays} pregões
        </DismissibleNotice>
      )}
      {automaticIntradayBlocked && (
        <DismissibleNotice noticeKey="kairi-intraday-automation-blocked" eventKey="blocked" closeLabel="Fechar aviso da automação intraday" className="kr-backtest-automation-note" role="status" data-testid="kairi-automatic-entry-status">
          Entrada automática intraday bloqueada para esta estratégia.
        </DismissibleNotice>
      )}
      {/* KPI Strip */}
      <div className="kr-kpi-strip">
        <div className="kr-kpi">
          <div className="kr-kpi-label">Monitorados</div>
          <div className="kr-kpi-value">{universeTickers.length}</div>
        </div>
        <div className="kr-kpi">
          <div className="kr-kpi-label">Setups confirmados</div>
          <div className="kr-kpi-value up">{counts.confirmed}</div>
        </div>
        <div className="kr-kpi">
          <div className="kr-kpi-label">Watchlist</div>
          <div className="kr-kpi-value purple">{counts.watching}</div>
        </div>
        <div className="kr-kpi">
          <div className="kr-kpi-label">Filtrados MM200</div>
          <div className="kr-kpi-value" style={{ color: '#5e6a85' }}>{counts.filtered}</div>
        </div>
         <div className="kr-kpi">
           <div className="kr-kpi-label">{expectancyLabel}</div>
           <div className={`kr-kpi-value ${netExpectancy >= 0 ? 'up' : 'down'}`}>
             {riskBacktest ? formatBacktestPercent(netExpectancy) : '—'}
           </div>
         </div>
         <div className="kr-kpi">
           <div className="kr-kpi-label">PF ponderado líquido</div>
           <div className={`kr-kpi-value ${riskBacktest && (riskBacktest.weightedNetProfitFactorInfinite || riskBacktest.weightedNetProfitFactor >= 1) ? 'up' : 'down'}`}>
             {riskBacktest?.weightedNetProfitFactorInfinite ? '∞' : formatWeightedProfitFactor({ ...riskBacktest, weightedProfitFactor: riskBacktest?.weightedNetProfitFactor, weightedProfitFactorInfinite: riskBacktest?.weightedNetProfitFactorInfinite })}
          </div>
        </div>
      </div>

      {/* Body */}
      {loading ? (
        <div className="kr-loading">
          <div className="kr-spinner" />
          <div>Calculando Kairi(25) · RSI · IBS para {universeTickers.length} ativos…</div>
          <div style={{ fontSize: 11, color: '#3d4860' }}>usando histórico acumulado no PostgreSQL e COTAHIST</div>
        </div>
      ) : error ? (
        <div className="kr-loading">
          <div style={{ color: '#ef5350', fontSize: 13 }}>⚠ {error}</div>
          <button
            onClick={loadAssets}
            style={{ padding: '8px 20px', background: '#26a69a', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600 }}
          >
            Tentar novamente
          </button>
        </div>
      ) : (
        <div className="kr-workspace">

          {/* ── Left Column ── */}
          <div className="kr-col-left">

            {selected && (
              <>
                {/* Confluence Card */}
                <div className="kr-panel">
                  <div className="kr-panel-head">
                    <div className="kr-panel-title">
                      <span className="kr-ticker-big mono">{selected.ticker}</span>
                      <span className="kr-meta">{selected.name}</span>
                      {selectedSig === 'buy'      && <span className="kr-setup-pill">Setup confirmado</span>}
                      {selectedSig === 'watch'    && <span className="kr-setup-pill watch">Aguardando</span>}
                      {selectedSig === 'filtered' && <span className="kr-setup-pill filtered">Filtrado MM200</span>}
                       {selected.needsReview       && <span className="kr-setup-pill watch">Revisar data ex</span>}
                    </div>
                    <div className="kr-conf-price-row">
                      <span className={`kr-conf-price-val${flashTickers.has(selected.ticker) ? ' ws-flash' : ''}`}>{fmtPrice(selected.price)}</span>
                      <span className={`${selected.change >= 0 ? 'up' : 'down'}${flashTickers.has(selected.ticker) ? ' ws-flash' : ''}`} style={{ fontSize: 11, fontFamily: "'JetBrains Mono', monospace", fontVariantNumeric: 'tabular-nums' }}>{fmtPct(selected.change)}</span>
                      <span className="kr-conf-trend-tag" style={{
                        background: selected.trendPct > 0 ? 'rgba(38,166,154,0.12)' : 'rgba(239,83,80,0.12)',
                        color: selected.trendPct > 0 ? '#26a69a' : '#ef5350',
                        border: `1px solid ${selected.trendPct > 0 ? 'rgba(38,166,154,0.4)' : 'rgba(239,83,80,0.4)'}`,
                      }}>
                        {selected.trendPct > 0 ? '↑' : '↓'} MM200 {fmtPct(selected.trendPct)}
                      </span>
                      {!selected.needsReview && (selectedSig === 'buy' || selectedSig === 'watch') && (
                        <button
                          className="kr-add-portfolio-btn"
                          onClick={() => setShowAddForm(true)}
                           disabled={selected.sectorContext?.diagnosis === 'idiosyncratic'}
                           title={selected.sectorContext?.diagnosis === 'idiosyncratic'
                             ? 'queda idiossincrática — não operar'
                             : backtestBlocked ? 'Exige confirmação de operação por sua conta.' : undefined}
                        >
                          + Carteira
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="kr-last-entry" aria-label="Último sinal de entrada">
                    <span>Último sinal de entrada</span>
                    {selected.lastEntryAvailable === false ? (
                      <strong>Histórico insuficiente</strong>
                    ) : selected.lastEntryReview ? (
                      <div>
                        <strong>Revisar data ex</strong>
                        <small>{fmtSignalDate(selected.lastEntryReview.date)} · entrada automática bloqueada</small>
                      </div>
                    ) : selected.lastEntry ? (
                      <div>
                        <strong>
                          {fmtSignalDate(selected.lastEntry.date)}
                          <span className="kr-last-entry-legacy">
                            Baseline / legado · {selected.lastEntry.variantLabel || selected.lastEntry.strategy || 'Kairi'}
                          </span>
                        </strong>
                        <small>
                          {fmtPrice(selected.lastEntry.price)} · Score {selected.lastEntry.score ?? '—'}/{selected.lastEntry.scoreMax ?? 100}
                        </small>
                      </div>
                    ) : (
                      <strong>Nenhum sinal confirmado no histórico</strong>
                    )}
                  </div>

                </div>

                {/* Stacked Charts */}
                {selected.kairiHistory.length >= 5 && (
                  <div className="kr-panel">
                    <div className="kr-panel-head">
                      <div className="kr-panel-title">
                        Indicadores — {selected.ticker}
                      </div>
                      <div style={{ display: 'flex', gap: 14, fontSize: 10, color: '#5e6a85' }}>
                        <span><span style={{ display: 'inline-block', width: 10, height: 2, background: '#5b9dff', marginRight: 5, verticalAlign: 'middle' }} />Kairi</span>
                        <span><span style={{ display: 'inline-block', width: 10, height: 2, background: '#a78bfa', marginRight: 5, verticalAlign: 'middle' }} />RSI(2)</span>
                        <span><span style={{ display: 'inline-block', width: 10, height: 8, background: '#26a69a', marginRight: 5, verticalAlign: 'middle', borderRadius: 1 }} />IBS</span>
                      </div>
                    </div>
                    <div className="kr-panel-body flush">
                      <StackedIndicators asset={selected} params={params} />
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Ranking Table */}
            <div className="kr-panel" style={{ flex: 1 }}>
              <div className="kr-panel-head">
                <div className="kr-panel-title">
                  Ranking por confluência
                  <span className="kr-meta">· {visible.length} ativos</span>
                </div>
                <div className="kr-seg">
                  {['Todos', 'Compra', 'Venda', 'Watch'].map(f => (
                    <button key={f} className={tableFilter === f ? 'on' : ''} onClick={() => setTableFilter(f)}>{f}</button>
                  ))}
                </div>
              </div>
              <div className="kr-panel-body flush kr-table-wrap">
                <table className="kr-t">
                  <thead>
                    <tr>
                      <th>Ativo</th>
                      <th>K · R · I · T · V · S</th>
                      <th className="num">Score</th>
                      <th className="num">Kairi</th>
                      <th className="num">RSI(2)</th>
                      <th className="num">IBS</th>
                      <th>Setup</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map(a => {
                      const sig      = classifyAsset(a, a.conf, params);
                       const cls      = a.conf.score >= 84 ? 'high' : a.conf.score >= 60 ? 'med' : 'low';
                      const sel      = a.ticker === selectedTicker;
                      const filtered = sig === 'filtered';
                      const sigLabel = { buy: 'Compra', sell: 'Venda', watch: 'Watch', filtered: 'Filtrado', skip: '—' }[sig];
                      const sigClass = { buy: 'buy', sell: 'sell', watch: 'watch', filtered: 'filtered', skip: 'neutral' }[sig];
                      const ch       = a.conf.checks;
                      return (
                        <tr
                          key={a.ticker}
                          className={`${sel ? 'sel' : ''} ${filtered ? 'filtered' : ''}`}
                          onClick={() => setSelectedTicker(a.ticker)}
                        >
                          <td>
                            <div className="kr-ticker-cell">
                              <div className={`kr-tk${flashTickers.has(a.ticker) ? ' ws-flash' : ''}`}>{a.ticker}</div>
                              <div className="kr-tn">{a.name}</div>
                            </div>
                          </td>
                          <td>
                            <div className="kr-sigil-row">
                              <span className={`kr-sigil ${ch[0].pass ? 'on' : 'off'}`}  title={ch[0].label}>K</span>
                              <span className={`kr-sigil ${ch[1].pass ? 'on' : 'off'}`}  title={ch[1].label}>R</span>
                              <span className={`kr-sigil ${ch[2].pass ? 'on' : ch[2].warn ? 'warn' : 'off'}`} title={ch[2].label}>I</span>
                              <span className={`kr-sigil ${ch[3].pass ? 'on' : 'fail'}`} title={ch[3].label}>T</span>
                               <span className={`kr-sigil ${ch[4].pass ? 'on' : ch[4].warn ? 'warn' : 'off'}`} title={ch[4].label}>V</span>
                               <span className={`kr-sigil ${ch[5]?.pass ? 'on' : ch[5]?.warn ? 'warn' : 'off'}`} title={ch[5]?.label || 'Contexto setorial'}>S</span>
                            </div>
                          </td>
                           <td className="num"><span className={`kr-score-pill ${cls}`}>{a.conf.score}/120</span></td>
                          <td className={`num ${a.kairi <= params.buyThreshold ? 'up' : a.kairi >= params.sellThreshold ? 'down' : ''}`}>
                            {fmtPct(a.kairi)}
                          </td>
                          <td className={`num ${a.rsi2 <= params.rsiThreshold ? 'up' : ''}`}>
                            {a.rsi2.toFixed(1)}
                          </td>
                          <td className={`num ${a.ibs <= params.ibsThreshold ? 'up' : ''}`}>
                            {a.ibs.toFixed(2)}
                          </td>
                          <td><span className={`kr-signal-tag ${sigClass}`}>{sigLabel}</span></td>
                        </tr>
                      );
                    })}
                    {visible.length === 0 && (
                      <tr><td colSpan={7} style={{ textAlign: 'center', color: '#5e6a85', padding: 24 }}>Nenhum ativo neste filtro</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* ── Right Column ── */}
          <div className="kr-col-right">

            {/* Score de Confluência */}
            {selected && (
              <div className="kr-panel">
                <div className="kr-panel-head">
                  <div className="kr-panel-title">
                    Score de confluência
                    <span className="kr-meta">· {selected.ticker}</span>
                  </div>
                </div>
                <div className="kr-right-score">
                  <div className="kr-right-score-top">
                    <ScoreCircle score={selected.conf.score} color={scoreColor} />
                    <div className="kr-right-score-meta">
                      <div className="kr-right-score-pct" style={{ color: scoreColor }}>
                         Score {selected.conf.score}/120
                      </div>
                      <div className="kr-right-score-status">
                        {selected.conf.score >= 96 ? 'Alta probabilidade' : selected.conf.score >= 72 ? 'Moderada' : selected.conf.score >= 48 ? 'Baixa' : 'Ignorar'}
                      </div>
                      <div className="kr-right-score-count">
                         Score {selected.conf.score}/120 · <strong>{selected.conf.passed}</strong> de <strong>{selected.conf.total}</strong> critérios
                        {selected.conf.partial ? ' (+ volume parcial)' : ''}
                      </div>
                    </div>
                  </div>

                  <div className="kr-right-score-divider" />

                  <div className="kr-right-score-checks">
                    {selected.conf.checks.map(c => (
                      <div key={c.id} className={`kr-right-check ${c.pass ? 'pass' : c.warn ? 'warn' : 'fail'}`}>
                        <div className="kr-right-check-icon">{c.pass ? '✓' : c.warn ? '◐' : '✕'}</div>
                        <div className="kr-right-check-label">{c.label}</div>
                        <div className="kr-right-check-value">{c.value}</div>
                      </div>
                    ))}
                  </div>

                  <SectorContextBlock asset={selected} />

                  {selectedSig === 'buy' && selected.sectorContext?.diagnosis !== 'idiosyncratic' && (
                    <div className="kr-right-score-confirmed">
                      Setup confirmado — entrada elegível na próxima abertura
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Portfolio Panel */}
            <div className="kr-panel">
              <div className="kr-panel-head">
                <div className="kr-panel-title">
                  Carteira teórica
                  {portfolio.length > 0 && <span className="kr-meta">· {portfolio.length} posições</span>}
                </div>
                {portfolioLoading && <div className="kr-port-loading" />}
              </div>
              <div className="kr-panel-body flush">
                {portfolio.length === 0 ? (
                  <div className="kr-port-empty">
                    Nenhuma posição ainda.<br />
                    Selecione um ativo com setup ativo e clique em <strong style={{ color: '#26a69a' }}>+ Carteira</strong>.
                  </div>
                ) : (
                  <>
                    <div className="kr-port-list">
                      {portfolio.map(p => {
                        const pnlPos = p.pnl_pct != null && p.pnl_pct >= 0;
                        const entryFmt = `R$ ${parseFloat(p.entry_price).toFixed(2).replace('.', ',')}`;
                        const dateFmt  = formatTradingDate(p.entry_date);
                        return (
                          <div
                            key={p.id}
                            className={`kr-port-row ${p.pnl_pct != null ? (pnlPos ? 'pos' : 'neg') : ''}`}
                            onClick={() => setSelectedTicker(p.ticker)}
                          >
                            <div className="kr-port-main">
                              <div className="kr-port-ticker">{p.ticker}</div>
                              <div className="kr-port-meta">
                                <span>{dateFmt}</span>
                                <span>·</span>
                                <span>{p.quantity} ações</span>
                                <span>·</span>
                                <span>entrada {entryFmt}</span>
                                {p.signal_score != null && <><span>·</span><span>{p.signal_score}%</span></>}
                              </div>
                              {p.notes && <div className="kr-port-meta" style={{ marginTop: 2, fontStyle: 'italic' }}>{p.notes}</div>}
                            </div>
                            <div className="kr-port-numbers">
                              <div className="kr-port-cur">
                                {p.current_price != null ? `R$ ${p.current_price.toFixed(2).replace('.', ',')}` : '—'}
                              </div>
                              <div className={`kr-port-pnl ${p.pnl_pct != null ? (pnlPos ? 'up' : 'down') : ''}`}>
                                {p.pnl_pct != null ? `${pnlPos ? '+' : ''}${p.pnl_pct.toFixed(2).replace('.', ',')}%` : '—'}
                              </div>
                            </div>
                            <button className="kr-port-remove" title="Remover" onClick={(e) => handleRemovePosition(p.id, e)}>✕</button>
                          </div>
                        );
                      })}
                    </div>
                    <div className="kr-port-summary">
                      <span>Investido</span>
                      <span className="kr-port-total">
                        R$ {portfolio.reduce((s, p) => s + p.quantity * parseFloat(p.entry_price), 0)
                          .toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </span>
                      {portfolio.some(p => p.pnl_r != null) && (() => {
                        const totalPnl = portfolio.reduce((s, p) => s + (p.pnl_r || 0), 0);
                        const isPos = totalPnl >= 0;
                        return (
                          <>
                            <span>P&amp;L total</span>
                            <span className={`kr-port-total ${isPos ? 'up' : 'down'}`}>
                              {isPos ? '+' : '−'}R$ {Math.abs(totalPnl).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                            </span>
                          </>
                        );
                      })()}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Signals */}
            <div className="kr-panel">
              <div className="kr-panel-head">
                <div className="kr-panel-title">Sinais detectados</div>
              </div>
              <div className="kr-panel-body flush" style={{ maxHeight: 320, overflowY: 'auto' }}>
                <div className="kr-alerts-list">
                  {alerts.length === 0 ? (
                    <div style={{ padding: '24px 14px', textAlign: 'center', color: '#5e6a85', fontSize: 11 }}>
                      Nenhum sinal ativo no momento
                    </div>
                  ) : alerts.map(a => (
                    <div
                      key={a.id}
                      className={`kr-alert-item ${a.kind}`}
                      onClick={() => a.ticker && setSelectedTicker(a.ticker)}
                    >
                      <div className="kr-alert-icon">{a.icon}</div>
                      <div>
                        <div className="kr-alert-title">{a.title}</div>
                        <div className="kr-alert-desc">{a.desc}</div>
                        {a.conf && <span className="kr-conf-chip">CONFLUÊNCIA {a.conf}%</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Performance */}
            <div className="kr-panel">
              <div className="kr-panel-head">
                <div className="kr-panel-title">
                  Performance
                   <span className="kr-meta">· {governanceBacktest || backtest ? `Backtest ${backtestPeriod ? `(${backtestPeriod})` : ''} · ${(governanceBacktest || backtest).tradingDays} pregões · ${(governanceBacktest || backtest).tradesTotal} trades` : '—'}</span>
                </div>
              </div>
              <div className="kr-panel-body flush">
                {backtest ? (
                  <>
                   <div className="kr-backtest-compare">
                     <BacktestColumn
                       title="Kairi + Confluência"
                        data={kairiGovernance?.baseline || backtests?.kairi || backtests?.baseline || backtests?.base || backtest}
                        active={kairiGovernance ? kairiGovernance.effectiveStrategy === 'kairi' : confluenceDecision?.activeConfluenceStrategy === 'kairi'}
                     />
                     <BacktestColumn
                       title="Kairi + Confluência + Setorial"
                        data={kairiGovernance?.candidate || backtests?.kairiSetorial || backtests?.setorial || backtests?.candidate}
                        active={kairiGovernance ? kairiGovernance.effectiveStrategy === 'kairi_setorial' : confluenceDecision?.activeConfluenceStrategy === 'kairi_setorial'}
                        shadow={kairiGovernance ? kairiGovernance.effectiveStrategy !== 'kairi_setorial' : confluenceDecision?.activeConfluenceStrategy !== 'kairi_setorial'}
                     />
                   </div>
                   {(strategyHealth?.strategies?.breakout55Blue || strategyHealth?.strategies?.breakout55Sc) && (
                     <div className="kr-breakout-backtest-row" data-testid="breakout55-backtest-row">
                       <strong>Rompimento 55/20</strong>
                       {[
                         ['BLUE', strategyHealth?.strategies?.breakout55Blue],
                         ['SC', strategyHealth?.strategies?.breakout55Sc],
                       ].map(([label, result]) => (
                         <span key={label}>
                           {label}: {result?.tradesTotal ?? result?.trades ?? '—'} trades · PF pond. {formatWeightedProfitFactor(result)} · líquida pond. {formatBacktestPercent(result?.weightedNetExpectancyPct)}
                           {result?.blocked ? ' · bloqueada' : ' · liberada'}
                         </span>
                       ))}
                     </div>
                   )}
                   <div className="kr-compare-grid">
                    <div className="kr-compare-stat">
                       <div className="lbl">Win rate simples</div>
                      <div className={`val ${backtest.winRate >= 50 ? 'up' : 'down'}`}>
                        {backtest.winRate.toFixed(1).replace('.',',')}%
                      </div>
                    </div>
                    <div className="kr-compare-stat">
                       <div className="lbl">PF simples</div>
                      <div className={`val ${backtest.profitFactorInfinite || backtest.profitFactor >= 1 ? 'up' : 'down'}`}>
                        {backtest.profitFactorInfinite ? '∞' : Number.isFinite(backtest.profitFactor) ? backtest.profitFactor.toFixed(1).replace('.',',') : '—'}
                      </div>
                    </div>
                    <div className="kr-compare-stat">
                       <div className="lbl">Win rate ponderado</div>
                       <div className={`val ${backtest.weightedWinRate >= 50 ? 'up' : 'down'}`}>
                         {Number.isFinite(backtest.weightedWinRate) ? `${backtest.weightedWinRate.toFixed(1).replace('.', ',')}%` : '—'}
                       </div>
                    </div>
                     <div className="kr-compare-stat">
                       <div className="lbl">PF ponderado líquido</div>
                       <div className={`val ${backtest.weightedNetProfitFactorInfinite || backtest.weightedNetProfitFactor >= 1 ? 'up' : 'down'}`}>
                         {backtest.weightedNetProfitFactorInfinite ? '∞' : formatWeightedProfitFactor({ ...backtest, weightedProfitFactor: backtest.weightedNetProfitFactor, weightedProfitFactorInfinite: backtest.weightedNetProfitFactorInfinite })}
                       </div>
                     </div>
                    <div className="kr-compare-stat">
                        <div className="lbl">Expectância simples bruta</div>
                       <div className={`val ${backtest.avgReturnPct >= 0 ? 'up' : 'down'}`}>
                         {formatBacktestPercent(backtest.avgReturnPct)}
                       </div>
                    </div>
                     <div className="kr-compare-stat">
                        <div className="lbl">Expectância ponderada líquida (custo por ativo (modelo v1))</div>
                       <div className={`val ${backtest.weightedNetExpectancyPct >= 0 ? 'up' : 'down'}`}>
                         {formatBacktestPercent(backtest.weightedNetExpectancyPct)}
                       </div>
                    </div>
                     <div className="kr-compare-stat">
                       <div className="lbl">Hold médio</div>
                       <div className="val">{backtest.avgHold.toFixed(1).replace('.',',')}d</div>
                     </div>
                     <div className="kr-compare-stat">
                       <div className="lbl">Pior trade</div>
                       <div className="val down">{(+backtest.worstTradePct).toFixed(1).replace('.',',')}%</div>
                     </div>
                     <div className="kr-compare-stat">
                       <div className="lbl">Trades/ano</div>
                       <div className="val">{backtest.tradesPerYear.toFixed(1).replace('.',',')}</div>
                     </div>
                   </div>
                  </>
                 ) : (
                  <div style={{ padding: '20px 0', textAlign: 'center', color: '#3d4860', fontSize: 11 }}>
                    Calculando backtest com dados reais...
                  </div>
                )}
              </div>
            </div>

            {/* Price Detail */}
            {selected && (
              <div className="kr-panel">
                <div className="kr-panel-head">
                  <div className="kr-panel-title">{selected.ticker} · preços</div>
                </div>
                <div className="kr-panel-body flush">
                  <div className="kr-engine-row">
                    <span className="lbl">Cotação</span>
                    <span className={`val${flashTickers.has(selected.ticker) ? ' ws-flash' : ''}`} style={{ fontSize: 15 }}>{fmtPrice(selected.price)}</span>
                  </div>
                  <div className="kr-engine-row">
                    <span className="lbl">Variação dia</span>
                    <span className={`val ${selected.change >= 0 ? 'buy' : 'sell'}${flashTickers.has(selected.ticker) ? ' ws-flash' : ''}`}>{fmtPct(selected.change)}</span>
                  </div>
                  <div className="kr-engine-row">
                    <span className="lbl">MM25</span>
                    <span className="val">{fmtPrice(selected.ma25)}</span>
                  </div>
                  <div className="kr-engine-row">
                    <span className="lbl">MM200</span>
                    <span className="val">{fmtPrice(selected.ma200)}</span>
                  </div>
                  <div className="kr-engine-row">
                    <span className="lbl">Dist. MM200</span>
                    <span className={`val ${selected.trendPct >= 0 ? 'buy' : 'sell'}`}>{fmtPct(selected.trendPct)}</span>
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>
      )}

      </>)}

      {/* ── Portfolio Modal ── */}
      {showAddForm && selected && (
        <PortfolioModal
          ticker={selected.ticker}
          name={selected.name}
          strategy="Kairi + Confluência"
          refs={[
            { label: 'Cotação atual', value: fmtPrice(selected.price) },
            { label: 'Kairi',         value: selected.kairi != null ? (selected.kairi > 0 ? '+' : '') + selected.kairi.toFixed(1) + '%' : '—', highlight: true },
            { label: 'RSI(2)',        value: selected.rsi2  != null ? selected.rsi2.toFixed(1)  : '—' },
            { label: 'Score',         value: selected.conf?.score != null ? selected.conf.score + '/120' : '—' },
          ]}
          defaultEntryPrice={selected.price}
          requiresOverride={backtestBlocked}
          strategyHealth={riskBacktest}
          onClose={() => setShowAddForm(false)}
          onSave={async (formData) => {
            await addKairiPosition({
              ticker:       selected.ticker,
              name:         selected.name,
              signal_score: selected.conf?.score ?? null,
              ...formData,
            });
            setShowAddForm(false);
            await loadPortfolio();
          }}
        />
      )}

    </div>
  );
}

export default KairiDashboard;
